import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './components/auth/LoginPage';
import POSSystem from './components/cashier/POSSystem';
import { LanguageProvider } from './context/LanguageContext';
import { QZTrayProviderWrapper } from './context/QZTrayContext';
import Dashboard from './pages/admin/Dashboard';
import CategoriesIndex from './pages/admin/categories/Index';
import CategoriesCreate from './pages/admin/categories/Create';
import CategoriesEdit from './pages/admin/categories/Edit';
import ItemsIndex from './pages/admin/items/Index';
import ItemsCreate from './pages/admin/items/Create';
import ItemsEdit from './pages/admin/items/Edit';
import OrdersIndex from './pages/admin/orders/Index';
import UsersIndex from './pages/admin/users/Index';
import CreateUser from './pages/admin/users/Create';
import EditUser from './pages/admin/users/Edit';
import Settings from './pages/admin/settings/Index';
import Reports from './pages/admin/Reports';

// Super Admin imports
import {
  AdminAuthProvider,
  AdminLayout,
  AdminProtectedRoute,
  AdminLogin,
  AdminDashboard,
  TenantList,
  TenantDetail,
  TenantCreate,
  AdminUsers,
} from './admin';

// Import styles
import './App.css';

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <QZTrayProviderWrapper>
          <Router>
          <Toaster
            position="top-right"
            toastOptions={{
              success: {
                style: {
                  background: '#34D399',
                  color: 'white',
                  padding: '16px',
                },
                iconTheme: {
                  primary: 'white',
                  secondary: '#34D399',
                },
              },
              error: {
                style: {
                  background: '#EF4444',
                  color: 'white',
                  padding: '16px',
                },
                iconTheme: {
                  primary: 'white',
                  secondary: '#EF4444',
                },
              },
            }}
          />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Root redirect */}
            <Route path="/" element={
              <ProtectedRoute requiredRole={null}>
                <RoleBasedRedirect />
              </ProtectedRoute>
            } />
            
            {/* Protected routes */}
            <Route path="/cashier" element={
              <ProtectedRoute requiredRole="cashier">
                <POSSystem />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole="admin">
                <Dashboard />
              </ProtectedRoute>
            } />

            {/* Categories Routes */}
            <Route path="/admin/categories" element={
              <ProtectedRoute requiredRole="admin">
                <CategoriesIndex />
              </ProtectedRoute>
            } />
            <Route path="/admin/categories/create" element={
              <ProtectedRoute requiredRole="admin">
                <CategoriesCreate />
              </ProtectedRoute>
            } />
            <Route path="/admin/categories/:id/edit" element={
              <ProtectedRoute requiredRole="admin">
                <CategoriesEdit />
              </ProtectedRoute>
            } />
            
            {/* Items Routes */}
            <Route path="/admin/items" element={
              <ProtectedRoute requiredRole="admin">
                <ItemsIndex />
              </ProtectedRoute>
            } />
            <Route path="/admin/items/create" element={
              <ProtectedRoute requiredRole="admin">
                <ItemsCreate />
              </ProtectedRoute>
            } />
            <Route path="/admin/items/:id/edit" element={
              <ProtectedRoute requiredRole="admin">
                <ItemsEdit />
              </ProtectedRoute>
            } />
            
            {/* Orders Routes */}
            <Route path="/admin/orders" element={
              <ProtectedRoute requiredRole="admin">
                <OrdersIndex />
              </ProtectedRoute>
            } />

            {/* Users Routes */}
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRole="admin">
                <UsersIndex />
              </ProtectedRoute>
            } />
            <Route path="/admin/users/create" element={
              <ProtectedRoute requiredRole="admin">
                <CreateUser />
              </ProtectedRoute>
            } />
            <Route path="/admin/users/:id/edit" element={
              <ProtectedRoute requiredRole="admin">
                <EditUser />
              </ProtectedRoute>
            } />

            {/* Settings Route */}
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredRole="admin">
                <Settings />
              </ProtectedRoute>
            } />

            {/* Reports Route */}
            <Route path="/admin/reports" element={
              <ProtectedRoute requiredRole="admin">
                <Reports />
              </ProtectedRoute>
            } />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Router>
        </QZTrayProviderWrapper>
      </LanguageProvider>
    </AuthProvider>
  );
}

// Super Admin App Component
export const SuperAdminApp = () => {
  return (
    <AdminAuthProvider>
      <Router>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/super-admin/login" element={<AdminLogin />} />
          <Route
            path="/super-admin"
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="tenants" element={<TenantList />} />
            <Route path="tenants/new" element={<TenantCreate />} />
            <Route path="tenants/:id" element={<TenantDetail />} />
            <Route path="users" element={<AdminUsers />} />
          </Route>
          <Route path="*" element={<Navigate to="/super-admin/login" replace />} />
        </Routes>
      </Router>
    </AdminAuthProvider>
  );
};

// Component to handle role-based redirects
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  
  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/cashier" replace />;
};

export default App;
